import React, { Component } from "react";
import PropTypes from "prop-types";

import styled from "styled-components";
import { Query } from "@apollo/client/react/components";
import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import { useTranslation } from "react-i18next";

import { GET_WINE_RATING, GET_AUTH, GET_MEMBER_LAYOUT_INFO } from "../../../graphql/queries";
import { UPDATE_PRODUCT_RATING } from "../../../graphql/resolvers/product";
import { getWineBaseColor, replaceInvalidApostrophe } from "../../../helpers/tools";
import { showErrorAlert, showSuccessAlert } from "../../../helpers/alerts";
import { logError } from "../../../helpers/logging";
import { FETCH_POLICY_CACHE_ONLY, I18N_NAMESPACES } from "../../../helpers/constants";
import breakpoints from "../../../styles/breakpoints";

import { Textarea } from "../../atoms/Input";
import Loading from "../../atoms/Loading";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import Button from "../../atoms/Button";
import GlobalAlert from "../../atoms/GlobalAlert/GlobalAlert";
import WineCardCellarRating from "../../atoms/WineCardCellarRating";
import TasteChallengeHeader from "../../atoms/TasteChallengeHeader";
import TextareaGhostDiv from "../../atoms/TextareaGhostDiv";
import {
  GridHolder,
  GridHolderItemHalf,
  GridHolderItemContent,
  GridHolderFooterContent,
} from "../../molecules/GridHolder";
import RotatingBottleBox from "../../atoms/RotatingBottleBox";
import { CellarBottleHolder } from "../../atoms/CellarBottleHolder";
import { ProductRatingDetailsBySlug } from "../../molecules/ProductRatingDetails";
import cellarBottleImg from "../../../assets/images/temp/bottle-xl-vertical.png";

const CellarGrid = styled.div`
  ${breakpoints.lgUp} {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  ${breakpoints.lgDown} {
    text-align: center;
  }
`;

const CellarGridText = styled.p`
  ${breakpoints.lgUp} {
    font-size: 16px;
    width: 40%;
    margin: 0px 20px 0px 0px;
  }
`;

const CellarGridComponentHolder = styled.div`
  ${breakpoints.lgUp} {
    display: flex;
    justify-content: flex-end;
    width: 60%;
    flex-shrink: 0;
  }
`;

const LoadingWrapper = styled.div`
  min-height: 100%;
  margin-top: 10vw;
`;

const MyCellarReviewHeader = styled(TasteChallengeHeader)``;

const BodySection = styled(GridHolderItemHalf)`
  ${(props) => props.css}
`;

const BodySectionContent = styled(GridHolderItemContent)`
  ${(props) => props.css}
`;

class MyCellarReviewBody extends Component {
  static propTypes = {
    authQuery: PropTypes.shape({}).isRequired,
    updateProductRating: PropTypes.func.isRequired,
    wine: PropTypes.shape({}).isRequired,
    setRandomVariableRewards: PropTypes.func.isRequired,
    bodySectionStyle: PropTypes.arrayOf(PropTypes.string),
    bodySectionContentStyle: PropTypes.arrayOf(PropTypes.string),
    onCloseButtonClick: PropTypes.func.isRequired,
    onSuccessfulUpdate: PropTypes.func.isRequired,
    toggleRefetchingAfterWineReview: PropTypes.func,
    onUpdateProductRatingSuccess: PropTypes.func,
  };

  static defaultProps = {
    bodySectionStyle: [],
    bodySectionContentStyle: [],
    toggleRefetchingAfterWineReview: null,
    onUpdateProductRatingSuccess: null,
  };

  state = {
    note: null,
    score: null,
    isSubmitInProgress: false,
    formErrorMessage: null,
    height: null,
  };

  /**
   * OnClick for setting a new rating score. We update the state to later use it on the mutation.
   *
   * @param {number} score
   */
  setNewRating = (score) => {
    this.setState({ score });
  };

  /**
   * OnChange for setting a new rating note. We update the state to later use it on the mutation.
   *
   * @param event
   */
  setNewNote = (event) => {
    const { value } = event.target;
    this.setState({ note: value });
  };

  /**
   * Handles the update of all the fields in the product rating. The mutation
   * response carries the updated product (CellarProductFragment), so the
   * cellar cache entry updates through normalisation — no manual cache write.
   *
   * @param {number} score
   * @param {string} note
   * */
  handleUpdateProductRating = (score, note) => {
    const {
      updateProductRating,
      wine,
      onSuccessfulUpdate,
      toggleRefetchingAfterWineReview,
      setRandomVariableRewards,
      t,
    } = this.props;

    if (!score) {
      this.setState({
        formErrorMessage: t("winePages:wineRating.selectScore"),
      });
      return;
    }
    this.setState({ isSubmitInProgress: true, formErrorMessage: null });

    toggleRefetchingAfterWineReview && toggleRefetchingAfterWineReview();

    updateProductRating({
      variables: {
        input: {
          productId: wine.product.id,
          score,
          note,
        },
      },
      refetchQueries: [{ query: GET_MEMBER_LAYOUT_INFO }],
      awaitRefetchQueries: true,
    })
      .then(({ data }) => {
        const { onUpdateProductRatingSuccess } = this.props;
        const { pointsEarned, randomVariableRewardPoints, errors } = data?.updateProductRating || {};

        const shouldShowRandomVariableRewardModal = !!(
          randomVariableRewardPoints && randomVariableRewardPoints > 0
        );

        const errorMessage = errors?.[0]?.messages?.[0];

        onUpdateProductRatingSuccess && onUpdateProductRatingSuccess(wine.product.id, score);

        if (errorMessage) {
          this.setState({ formErrorMessage: errorMessage, isSubmitInProgress: false });
        } else {
          if (pointsEarned && pointsEarned > 0) {
            shouldShowRandomVariableRewardModal && setRandomVariableRewards(randomVariableRewardPoints);
            showSuccessAlert(t("winePages:wineRating.thankYouNew", { pointsEarned }));
          } else {
            showSuccessAlert(t("winePages:wineRating.thankYouUpdate"));
          }
          /* The saved review is announced by the global toast (it outlives
             this pane); the pane itself slides out as soon as the save
             lands — the button holds its submitting state through the
             animation. */
          onSuccessfulUpdate();
        }

        // Refetches the query with flag shouldUpdateLimit=true to refetch up to the point whether the user
        // has scrolled to.
        toggleRefetchingAfterWineReview && toggleRefetchingAfterWineReview(true);
      })
      .catch(() => {
        this.setState({ isSubmitInProgress: false });
        showErrorAlert(t("common:sorryTryAgain"));
        toggleRefetchingAfterWineReview && toggleRefetchingAfterWineReview();
      });
  };

  /**
   * this function gets the height of the invisible
   * ..ghost div with text input content, and sets that height in the state,
   * ..so, it can be used for the varying height for Textarea component.
   *
   * */
  setFilledTextareaHeight = () => {
    if (this.mounted) {
      const element = this.ghostDiv;
      this.setState({
        height: element.clientHeight,
      });
    }
  };

  render() {
    const { authQuery, wine, bodySectionStyle, bodySectionContentStyle, onCloseButtonClick, t } = this.props;

    if (authQuery.loading) {
      return (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      );
    }

    const { memberId } = authQuery.auth;
    const { wineType } = wine || {};
    const { id: wineTypeId, wineClass, name: wineTypeName } = wineType || {};
    const wineTypeContext = wineTypeName === "Sake" ? "sake" : "";
    const { id: wineClassId } = wineClass || {};
    const baseColor = getWineBaseColor(wineClassId, wineTypeId);

    return (
      <GridHolder data-testid="cellar-review-body">
        <MyCellarReviewHeader
          onCloseButtonClick={onCloseButtonClick}
          title={t("winePages:wineRating.reviewAndRate")}
          center
          isBackButtonCross={false}
        />
        <CellarBottleHolder baseColor={baseColor}>
          <RotatingBottleBox
            imgUrl={
              (wine.product.productPhotos.length && wine.product.productPhotos[0].photoLarge) ||
              cellarBottleImg
            }
            rotateMobile
          />
        </CellarBottleHolder>

        <BodySection css={bodySectionStyle}>
          <BodySectionContent css={bodySectionContentStyle}>
            <Query query={GET_WINE_RATING} variables={{ memberId, wineId: wine.id }}>
              {({ loading, error, data }) => {
                if (loading) return <StyledLoading />;
                if (error) logError(error, { location: "MyCellarReviewBody", action: "GET_WINE_RATING" });

                const { isSubmitInProgress, formErrorMessage, height } = this.state;
                let { score, note } = this.state;
                this.mounted = true;
                if (!score) {
                  // memberWineRatingScore carries half-star precision; wineRating.score
                  // is the whole-star legacy value, kept as a fallback. `data` itself is
                  // undefined when the query errored (the branch above only logs).
                  score = data?.wine?.memberWineRatingScore ?? data?.wineRating?.score;
                }
                if (note === null && data?.wineRating) {
                  ({ note } = data.wineRating);
                }
                note = replaceInvalidApostrophe(note || "");
                const pointsPerBottleRated = data?.me?.level?.pointsPerBottleRated;

                const textFieldPlaceholder = t("winePages:wineRating.textField", {
                  context: wineTypeContext,
                });

                return (
                  <>
                    <CellarGrid>
                      <CellarGridText>
                        {t("winePages:wineRating.willEarn", {
                          pointsPerBottleRated,
                          context: wineTypeContext,
                        })}
                      </CellarGridText>

                      <CellarGridComponentHolder>
                        <WineCardCellarRating largeIcon rating={score} onClickRating={this.setNewRating} />
                      </CellarGridComponentHolder>
                    </CellarGrid>
                    {/* The detail rating rows — fully editable here, sized to
                        match the form (the note lives in the form's own
                        textarea below). */}
                    <ProductRatingDetailsBySlug slug={wine.product.slug} editable />
                    <div>
                      <Textarea
                        name="note"
                        placeholder={textFieldPlaceholder}
                        onChange={this.setNewNote}
                        value={note !== null ? note : ""}
                        onKeyUp={this.setFilledTextareaHeight}
                        isResizableText
                        customHeight={`${height}px`}
                      />
                      <TextareaGhostDiv
                        ref={(domObj) => {
                          this.ghostDiv = domObj;
                        }}
                        aria-hidden="true"
                      >
                        {note !== null ? note : ""}
                      </TextareaGhostDiv>
                    </div>

                    <GridHolderFooterContent>
                      {formErrorMessage && <GlobalAlert message={formErrorMessage} type="error" />}

                      <Button full lg onClick={() => this.handleUpdateProductRating(score, note)}>
                        {isSubmitInProgress
                          ? t("winePages:wineRating.submitting")
                          : t("winePages:wineRating.submit")}
                      </Button>
                    </GridHolderFooterContent>
                  </>
                );
              }}
            </Query>
          </BodySectionContent>
        </BodySection>
      </GridHolder>
    );
  }
}

const MyCellarReviewBodyWrapper = (props) => {
  const { t } = useTranslation([I18N_NAMESPACES.WINE_PAGES]);
  return <MyCellarReviewBody {...props} t={t} />;
};

export default compose(
  graphql(GET_AUTH, {
    name: "authQuery",
    options: {
      fetchPolicy: FETCH_POLICY_CACHE_ONLY,
    },
  }),
  graphql(UPDATE_PRODUCT_RATING, {
    name: "updateProductRating",
  })
)(MyCellarReviewBodyWrapper);
